export type BlobHatType = 'wizard' | 'helmet' | 'hood' | 'horns' | 'none';
export type BlobWeaponType = 'staff' | 'sword' | 'bow' | 'potion' | 'dagger' | 'mace' | 'none';
export type BlobAccessoryType = 'orb' | 'shield' | 'leaves' | 'spikes' | 'collar' | 'book' | 'none';

export interface BlobMobConfig {
  bodyColor: string;
  faceColor: string;
  shadowColor: string;
  rimColor: string;
  accentColor: string;
  accentColorSecondary?: string;
  eyeColor?: string;
  hatType: BlobHatType;
  weaponType: BlobWeaponType;
  accessoryType: BlobAccessoryType;
  bodyScale?: number;
  bodyYOffset?: number;
}

export function drawBlobShadowRing(
  ctx: CanvasRenderingContext2D,
  size: number,
  color: string
): void {
  ctx.beginPath();
  ctx.ellipse(0, size * 0.28, size * 0.29, size * 0.11, 0, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}

export function drawBlobBody(
  ctx: CanvasRenderingContext2D,
  size: number,
  config: BlobMobConfig
): void {
  const bodyScale = config.bodyScale ?? 1;
  const bodyYOffset = config.bodyYOffset ?? 0;
  const bodyRadius = size * 0.3 * bodyScale;
  const bodyY = size * 0.03 + bodyYOffset;
  const faceY = bodyY - bodyRadius * 0.28;

  ctx.beginPath();
  ctx.arc(0, bodyY, bodyRadius, 0, Math.PI * 2);
  ctx.fillStyle = config.bodyColor;
  ctx.fill();

  const highlight = ctx.createRadialGradient(
    -bodyRadius * 0.2,
    faceY - bodyRadius * 0.18,
    bodyRadius * 0.18,
    0,
    bodyY,
    bodyRadius * 1.1
  );
  highlight.addColorStop(0, 'rgba(255, 255, 255, 0.28)');
  highlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = highlight;
  ctx.beginPath();
  ctx.arc(0, bodyY, bodyRadius, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(0, faceY, bodyRadius * 0.58, bodyRadius * 0.28, 0, 0, Math.PI * 2);
  ctx.fillStyle = config.faceColor;
  ctx.fill();
}

export function drawBlobEyes(
  ctx: CanvasRenderingContext2D,
  size: number,
  config: BlobMobConfig
): void {
  const eyeColor = config.eyeColor ?? '#2d140c';
  const eyeY = size * -0.075 + (config.bodyYOffset ?? 0);
  const eyeOffset = size * 0.1;
  const eyeRadius = size * 0.034;
  for (const direction of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(direction * eyeOffset, eyeY, eyeRadius, 0, Math.PI * 2);
    ctx.fillStyle = eyeColor;
    ctx.fill();
  }
}

export function drawBlobHat(
  ctx: CanvasRenderingContext2D,
  size: number,
  config: BlobMobConfig
): void {
  switch (config.hatType) {
    case 'wizard': {
      ctx.beginPath();
      ctx.moveTo(-size * 0.23, -size * 0.11);
      ctx.quadraticCurveTo(0, -size * 0.17, size * 0.22, -size * 0.1);
      ctx.quadraticCurveTo(size * 0.09, -size * 0.03, -size * 0.22, -size * 0.04);
      ctx.closePath();
      ctx.fillStyle = config.accentColorSecondary ?? '#274892';
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(-size * 0.07, -size * 0.1);
      ctx.quadraticCurveTo(size * 0.02, -size * 0.42, size * 0.13, -size * 0.18);
      ctx.lineTo(size * 0.03, -size * 0.08);
      ctx.closePath();
      ctx.fillStyle = config.accentColorSecondary ?? '#355fc7';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(size * 0.045, -size * 0.245, size * 0.022, 0, Math.PI * 2);
      ctx.fillStyle = config.accentColor;
      ctx.fill();

      ctx.fillStyle = config.rimColor;
      ctx.fillRect(-size * 0.15, -size * 0.112, size * 0.27, size * 0.028);
      return;
    }
    case 'helmet': {
      ctx.beginPath();
      ctx.arc(0, -size * 0.015, size * 0.2, Math.PI, Math.PI * 2);
      ctx.fillStyle = config.accentColorSecondary ?? '#788397';
      ctx.fill();

      ctx.beginPath();
      ctx.moveTo(-size * 0.15, -size * 0.03);
      ctx.lineTo(size * 0.15, -size * 0.03);
      ctx.lineTo(size * 0.1, size * 0.085);
      ctx.lineTo(-size * 0.1, size * 0.085);
      ctx.closePath();
      ctx.fillStyle = config.accentColorSecondary ?? '#4c5566';
      ctx.fill();

      ctx.strokeStyle = '#dbe4f0';
      ctx.lineWidth = size * 0.016;
      for (const x of [-0.08, -0.03, 0.02, 0.07]) {
        ctx.beginPath();
        ctx.moveTo(size * x, -size * 0.005);
        ctx.lineTo(size * x, size * 0.067);
        ctx.stroke();
      }
      return;
    }
    case 'hood': {
      ctx.beginPath();
      ctx.moveTo(-size * 0.24, -size * 0.04);
      ctx.quadraticCurveTo(-size * 0.15, -size * 0.34, size * 0.02, -size * 0.28);
      ctx.quadraticCurveTo(size * 0.2, -size * 0.22, size * 0.22, -size * 0.02);
      ctx.quadraticCurveTo(size * 0.15, size * 0.13, 0, size * 0.15);
      ctx.quadraticCurveTo(-size * 0.15, size * 0.13, -size * 0.24, -size * 0.04);
      ctx.closePath();
      ctx.fillStyle = config.accentColorSecondary ?? '#5d7b32';
      ctx.fill();
      return;
    }
    case 'horns': {
      for (const side of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(side * size * 0.08, -size * 0.16);
        ctx.quadraticCurveTo(side * size * 0.22, -size * 0.31, side * size * 0.16, -size * 0.07);
        ctx.closePath();
        ctx.fillStyle = config.rimColor;
        ctx.fill();
      }
      return;
    }
    default:
      return;
  }
}

export function drawBlobWeapon(
  ctx: CanvasRenderingContext2D,
  size: number,
  config: BlobMobConfig
): void {
  switch (config.weaponType) {
    case 'staff': {
      ctx.save();
      ctx.rotate((28 * Math.PI) / 180);
      ctx.fillStyle = '#5a3822';
      ctx.fillRect(size * 0.16, -size * 0.22, size * 0.036, size * 0.55);
      ctx.fillStyle = config.accentColor;
      ctx.fillRect(size * 0.15, -size * 0.27, size * 0.06, size * 0.08);
      ctx.restore();
      return;
    }
    case 'sword': {
      ctx.save();
      ctx.rotate((40 * Math.PI) / 180);
      ctx.fillStyle = '#d8dee9';
      ctx.fillRect(size * 0.11, -size * 0.18, size * 0.045, size * 0.35);
      ctx.beginPath();
      ctx.moveTo(size * 0.11, -size * 0.18);
      ctx.lineTo(size * 0.155, -size * 0.28);
      ctx.lineTo(size * 0.2, -size * 0.18);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#9a6334';
      ctx.fillRect(size * 0.08, size * 0.11, size * 0.1, size * 0.03);
      ctx.fillRect(size * 0.11, size * 0.11, size * 0.035, size * 0.09);
      ctx.restore();
      return;
    }
    case 'bow': {
      ctx.strokeStyle = '#6a4220';
      ctx.lineWidth = size * 0.026;
      ctx.beginPath();
      ctx.arc(size * 0.19, size * 0.01, size * 0.14, -Math.PI * 0.72, Math.PI * 0.72);
      ctx.stroke();
      ctx.strokeStyle = '#ecfdf5';
      ctx.lineWidth = size * 0.012;
      ctx.beginPath();
      ctx.moveTo(size * 0.09, -size * 0.09);
      ctx.lineTo(size * 0.22, size * 0.12);
      ctx.stroke();
      return;
    }
    case 'potion': {
      ctx.beginPath();
      ctx.arc(size * 0.23, size * 0.01, size * 0.065, 0, Math.PI * 2);
      ctx.fillStyle = config.accentColor;
      ctx.fill();
      ctx.fillStyle = '#dbeafe';
      ctx.fillRect(size * 0.212, -size * 0.075, size * 0.035, size * 0.04);
      return;
    }
    case 'dagger': {
      ctx.save();
      ctx.rotate((26 * Math.PI) / 180);
      ctx.fillStyle = '#f1f5f9';
      ctx.fillRect(size * 0.12, -size * 0.06, size * 0.035, size * 0.22);
      ctx.beginPath();
      ctx.moveTo(size * 0.12, -size * 0.06);
      ctx.lineTo(size * 0.137, -size * 0.15);
      ctx.lineTo(size * 0.155, -size * 0.06);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#8b5a2b';
      ctx.fillRect(size * 0.095, size * 0.12, size * 0.08, size * 0.022);
      ctx.fillRect(size * 0.116, size * 0.12, size * 0.028, size * 0.065);
      ctx.restore();
      return;
    }
    case 'mace': {
      ctx.save();
      ctx.rotate((34 * Math.PI) / 180);
      ctx.fillStyle = '#9a6334';
      ctx.fillRect(size * 0.12, -size * 0.18, size * 0.03, size * 0.36);
      ctx.beginPath();
      ctx.arc(size * 0.135, -size * 0.21, size * 0.055, 0, Math.PI * 2);
      ctx.fillStyle = config.accentColorSecondary ?? '#dfe7ef';
      ctx.fill();
      ctx.restore();
      return;
    }
    default:
      return;
  }
}

export function drawBlobAccessory(
  ctx: CanvasRenderingContext2D,
  size: number,
  config: BlobMobConfig
): void {
  switch (config.accessoryType) {
    case 'orb': {
      ctx.beginPath();
      ctx.arc(size * 0.22, size * 0.09, size * 0.05, 0, Math.PI * 2);
      ctx.fillStyle = config.accentColor;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(size * 0.235, size * 0.075, size * 0.018, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.fill();
      return;
    }
    case 'shield': {
      ctx.beginPath();
      ctx.moveTo(-size * 0.22, -size * 0.02);
      ctx.lineTo(-size * 0.11, -size * 0.02);
      ctx.lineTo(-size * 0.08, size * 0.11);
      ctx.lineTo(-size * 0.165, size * 0.18);
      ctx.lineTo(-size * 0.25, size * 0.11);
      ctx.closePath();
      ctx.fillStyle = config.accentColorSecondary ?? '#96a4b8';
      ctx.fill();
      return;
    }
    case 'leaves': {
      for (const x of [-0.11, 0, 0.11]) {
        ctx.beginPath();
        ctx.ellipse(size * x, -size * 0.19, size * 0.055, size * 0.095, size * x * 1.8, 0, Math.PI * 2);
        ctx.fillStyle = config.accentColor;
        ctx.fill();
      }
      return;
    }
    case 'spikes': {
      for (const x of [-0.17, -0.06, 0.06, 0.17]) {
        ctx.beginPath();
        ctx.moveTo(size * x, -size * 0.18);
        ctx.lineTo(size * (x + 0.04), -size * 0.27);
        ctx.lineTo(size * (x + 0.08), -size * 0.18);
        ctx.closePath();
        ctx.fillStyle = config.rimColor;
        ctx.fill();
      }
      return;
    }
    case 'collar': {
      ctx.beginPath();
      ctx.moveTo(-size * 0.18, size * 0.11);
      ctx.quadraticCurveTo(0, size * 0.17, size * 0.18, size * 0.11);
      ctx.lineTo(size * 0.12, size * 0.19);
      ctx.quadraticCurveTo(0, size * 0.15, -size * 0.12, size * 0.19);
      ctx.closePath();
      ctx.fillStyle = config.accentColorSecondary ?? '#203a74';
      ctx.fill();
      return;
    }
    case 'book': {
      ctx.fillStyle = config.accentColorSecondary ?? '#3b1f56';
      ctx.fillRect(-size * 0.27, size * 0.02, size * 0.11, size * 0.12);
      ctx.fillStyle = config.accentColor;
      ctx.fillRect(-size * 0.258, size * 0.03, size * 0.086, size * 0.096);
      ctx.strokeStyle = '#f8fafc';
      ctx.lineWidth = size * 0.01;
      ctx.beginPath();
      ctx.moveTo(-size * 0.215, size * 0.03);
      ctx.lineTo(-size * 0.215, size * 0.126);
      ctx.stroke();
      return;
    }
    default:
      return;
  }
}

export function drawBlobMobProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number,
  config: BlobMobConfig
): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(centerX, centerY);
  ctx.rotate((rotation * Math.PI) / 180);

  ctx.shadowColor = config.shadowColor;
  ctx.shadowBlur = size * 0.16;
  drawBlobShadowRing(ctx, size, config.shadowColor);
  drawBlobBody(ctx, size, config);
  ctx.shadowBlur = 0;
  drawBlobAccessory(ctx, size, config);
  drawBlobHat(ctx, size, config);
  drawBlobWeapon(ctx, size, config);
  drawBlobEyes(ctx, size, config);

  ctx.lineWidth = size * 0.018;
  ctx.strokeStyle = config.rimColor;
  ctx.beginPath();
  ctx.arc(0, size * 0.03 + (config.bodyYOffset ?? 0), size * 0.3 * (config.bodyScale ?? 1), Math.PI * 0.12, Math.PI * 0.88);
  ctx.stroke();

  ctx.restore();
}
